import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import { Form, Field } from 'mobx-react-form';

import { useRootStore } from 'stores/RootStore';

import { withMicrofrontend } from 'utils/microfrontend/useMicrofrontend';
import { CompPlexCommercialLeaseEvents } from '../constants';
import { mapCompPlexCommercialLeaseToRentComp } from './mappers';
import { RentCompsTable } from './RentCompsTable';
import { checkIfCommercialRentCompsAreLatestVersion } from './helpers';
import { CommercialRentComp, CommercialRentRollUnit } from 'types/underwriting/income-approach';
import { removeArrayItemByValue } from 'components/shared/form/utils';
import { getSubjectUnitGroups, groupCompsByUse } from '../helpers';

type CommercialRentCompsProps = {
  form: Form;
  compplexLoaded: boolean;
};

const CommercialRentComps: React.FC<CommercialRentCompsProps> = ({ form, compplexLoaded }) => {
  const { authStore, underwritingPageStore } = useRootStore();
  const { underwritingData } = underwritingPageStore;
  const { currentUser } = authStore;
  // TODO: figure out how we're handling CoStar, for now we're just setting it to false
  // const hasCostarLicense = get(currentUser, 'hasCostarLicense', false);
  const hasCostarLicense = false;
  const rentCompsField: Field = form.$('incomeApproach.commercial.rentComps.comps');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rentComps: CommercialRentComp[] = rentCompsField.values() || [];
  const rentRoll: CommercialRentRollUnit[] = useMemo(() => {
    return underwritingData?.incomeApproach?.commercial?.rentRoll || [];
  }, [underwritingData]);

  const subjectUnitGroups = useMemo(() => {
    return getSubjectUnitGroups(rentRoll);
  }, [rentRoll]);

  const [viewModalComp, setViewModalComp] = useState<{
    leaseId?: string | null;
    leaseVersionNumber?: string | null;
  } | null>({
    leaseId: null,
    leaseVersionNumber: null,
  });
  const [viewModalToggle, setViewModalToggle] = useState(false);

  const compplexRef = useRef<CompPlexElement>(null);

  const subjectProps = {
    coordinates: {
      lat: underwritingData?.property.coords?.latitude,
      lng: underwritingData?.property.coords?.longitude,
    },
  };

  const handleUpdateCompToLatestVersion = useCallback(
    (event: any) => {
      const { residentialRentComp } = event.detail;
      const updatedComp = mapCompPlexCommercialLeaseToRentComp(residentialRentComp);
      rentCompsField.each((field: Field) => {
        if (field.value.leaseId === updatedComp.leaseId) {
          field.set(updatedComp);
        }
      });
    },
    [rentCompsField]
  );

  const removeComp = useCallback(
    (removedComp: { leaseId: string }) => {
      removeArrayItemByValue(form, rentCompsField, 'leaseId', removedComp.leaseId);
    },
    [form, rentCompsField]
  );

  const handleRemoveComp = useCallback(
    (event: any) => {
      const { leaseId } = event.detail;
      removeComp({ leaseId });
    },
    [removeComp]
  );

  const handleAddComp = useCallback(
    (event: any) => {
      const { commercialRentComp } = event.detail;
      const mappedCommercialRentComp = mapCompPlexCommercialLeaseToRentComp(commercialRentComp);
      rentCompsField.add([mappedCommercialRentComp]);
    },
    [rentCompsField]
  );

  const showViewModal = useCallback(
    (comp: any) => {
      setViewModalComp(comp);
      setViewModalToggle(true);
    },
    [setViewModalComp, setViewModalToggle]
  );

  const hideViewModal = useCallback(() => {
    setViewModalComp(null);
    setViewModalToggle(false);
  }, [setViewModalComp, setViewModalToggle]);

  const checkIfCompsAreLatestVersion = useCallback(async () => {
    const updatedComps = await checkIfCommercialRentCompsAreLatestVersion(rentComps);
    rentCompsField.set(updatedComps);
  }, [rentComps, rentCompsField]);

  useEffect(() => {
    const handleError = (event: any) => {
      const { error } = event.detail;
      console.error(error.message || error);
    };

    let currentRef: CompPlexElement | null;
    if (compplexRef.current) {
      compplexRef.current.addEventListener(
        CompPlexCommercialLeaseEvents.EDIT_COMP_UPDATED,
        handleUpdateCompToLatestVersion
      );
      compplexRef.current.addEventListener(CompPlexCommercialLeaseEvents.FOUND_COMP_ADDED, handleAddComp);
      compplexRef.current.addEventListener(CompPlexCommercialLeaseEvents.EDIT_COMP_CLOSED, hideViewModal);
      compplexRef.current.addEventListener(CompPlexCommercialLeaseEvents.MAP_COMP_ADDED, handleAddComp);
      compplexRef.current.addEventListener(CompPlexCommercialLeaseEvents.MAP_COMP_REMOVED, handleRemoveComp);
      compplexRef.current.addEventListener(CompPlexCommercialLeaseEvents.ERROR, handleError);

      currentRef = compplexRef.current;
    }

    checkIfCompsAreLatestVersion();

    return () => {
      if (currentRef) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        compplexRef.current?.removeEventListener(
          CompPlexCommercialLeaseEvents.EDIT_COMP_UPDATED,
          handleUpdateCompToLatestVersion
        );
        currentRef.removeEventListener(CompPlexCommercialLeaseEvents.FOUND_COMP_ADDED, handleAddComp);
        currentRef.removeEventListener(CompPlexCommercialLeaseEvents.EDIT_COMP_CLOSED, hideViewModal);
        currentRef.removeEventListener(CompPlexCommercialLeaseEvents.MAP_COMP_ADDED, handleAddComp);
        currentRef.removeEventListener(CompPlexCommercialLeaseEvents.MAP_COMP_REMOVED, handleRemoveComp);
        currentRef.removeEventListener(CompPlexCommercialLeaseEvents.ERROR, handleError);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const compGroups = groupCompsByUse(rentComps);

  const selectedRentCompIds = rentComps.map((comp) => ({
    id: comp.leaseId,
  }));

  const viewModalProps = {
    showEditModal: viewModalToggle,
    leaseId: viewModalComp?.leaseId || null,
    leaseVersionNumber: viewModalComp?.leaseVersionNumber || null,
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h6">Commercial Rent Comps</Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {compplexLoaded ? (
                <comp-plex-commercial-lease
                  ref={compplexRef}
                  auth-user={JSON.stringify(currentUser)}
                  client-portal={true}
                  has-costar-license={hasCostarLicense}
                  edit-lease-props={JSON.stringify(viewModalProps)}
                  subject={JSON.stringify(subjectProps)}
                  selected-comps={JSON.stringify(selectedRentCompIds)}
                />
              ) : (
                <Typography variant="body1" align="center" py={4}>
                  Loading Comp Database...
                </Typography>
              )}
            </Grid>
            {compGroups.map(({ use, comps }) => {
              return (
                <Grid item xs={12} key={`rent-comps-group-${use}`}>
                  <RentCompsTable
                    subjectUnits={subjectUnitGroups.find((group) => group.use === use)?.units || []}
                    comps={comps}
                    name={use}
                    removeComp={removeComp}
                    showViewModal={showViewModal}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export const CommercialRentCompsWithCompplex = withMicrofrontend('compplex', 'compplexLoaded')(CommercialRentComps);
